import i18n from '@i18next/i18n';
import { loadCommentEditable } from '@src/js/common/ark-extensions/dc-components/docs-component-comment-editable';
import Button from '@tencent/dui/lib/components/Button';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { IAddOrUpdateCommentApi } from '../../../type';
import Style from '../index.less';
import { ICommentEditable, ICommentEditableOps, InputCommitStatus } from './type';
import { formatEditorContent } from './utils';

interface ICommentInputProps extends Partial<IAddOrUpdateCommentApi> {
    content: string;
    readOnly: boolean;
    groupId: string;
    commentId?: string;
    commentGroupItemWrapperRef?: any;
    onFocus?: () => void;
    onBlur?: () => void;
    onEditStateChanged?: (commentId?: string, isEditing?: boolean) => void;
}

export const editorElements = ['id', 'mention', 'data-avatar'];

// 批注长度不能超过1000个字符兼容回车
export const MAX_CONTENT_LENGTH = 1000;

const CommentInput: React.FC<ICommentInputProps> = (props: ICommentInputProps) => {
    const [commentEditable, setCommentEditable] = useState<any>(null);
    const [hasContent, setHasContent] = useState(false);
    const [commentUserInfo, setCommentUserInfo] = useState({});
    const [inputCommitStatus, setInputCommitStatus] = useState(InputCommitStatus.INVALID);
    const [isOverLength, setIsOverLength] = useState(false);
    const inputRef = useRef<ICommentEditable>(null);
    const isMounted = useRef(true);
    const isFocus = useRef(false);
    const historyIndexRef = useRef(0);

    useEffect(() => {
        init();
        return () => {
            isMounted.current = false;
            inputRef?.current?.container?.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const init = async () => {
        if (!isMounted.current) return;
        const commentEditableExtension = await loadCommentEditable();
        setCommentEditable(commentEditableExtension);
    };

    const renderCommentEditable = () => {
        // 注意：开始聚焦&输入文本的时候，调用事件的顺序如下
        // - onEditorChange
        // - onChangeSelection
        // - onFocus
        // - onKeydown
        // - onInput
        // 其中changeSelection和handleEditorChange会在编辑过程中多次出现
        // handleEditorChange之后会伴随changeSelection或handleInput
        // 了解编辑器组件的事件发送顺序，有助于避免批注功能出现预期外的BUG
        const CommentEditable = commentEditable?.getComponentConstructor();
        if (!CommentEditable) return null;
        const defaultValue = formatEditorContent(props.content, commentUserInfo);
        return <CommentEditable
            maxLength={MAX_CONTENT_LENGTH}
            spellCheck={false}
            onInput={handleInput}
            onSelect={() => { }}
            placeholder={i18n.t('添加批注')}
            defaultValue={defaultValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onEditorChange={handleEditorChange}
            initEditor={initEditor}
            formats={editorElements}
            readOnly={!!props.readOnly}
        />;
    };

    const initEditor = (editor: ICommentEditable) => {
        // @ts-ignore
        inputRef.current = editor;
        // 在此处监听，因为组件的onKeydown事件的event.isComposing始终为undefined
        inputRef.current.container?.addEventListener('keydown', handleKeyDown);
    };

    const handleEditorChange = () => {
        if (inputRef.current) {
            const selection = inputRef.current.getSelection();
            if (selection) {
                const { index } = selection;
                historyIndexRef.current = index;
            }
        }
    };

    const handleInput = () => {
        if (props.readOnly) return;
        setTimeout(() => {
            const inputLength = inputRef.current?.getLength();
            if (typeof inputLength === 'number') {
                setIsOverLength(inputLength > MAX_CONTENT_LENGTH);
            }
            props.onEditStateChanged?.(props.commentId, true);
            setHasContent(getOriginalContent().length > 0);
        });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.isComposing) {
            setInputCommitStatus(InputCommitStatus.PENDING);
        }
        if (shouldBypassKeyDown(event)) return;

        switch (event.key.toLowerCase()) {
            case 'enter':
                event.preventDefault();
                event.stopPropagation();
                if (isNewLineModifyKeyPressed(event)) {
                    handleNewLine();
                    return;
                }
                handleConfirm();
                break;
            default:
                break;
        }
        setInputCommitStatus(InputCommitStatus.NORMAL_INPUT_DONE);
    };

    const shouldBypassKeyDown = (event: KeyboardEvent) =>
        // 组件没有挂载成功
        !isMounted.current
        // textarea不是焦点
        || !isFocus.current
        // 输入法正在输入
        || event.isComposing;

    /**
     * 按下(control/alt/meta) + enter都可以换行
     */
    const isNewLineModifyKeyPressed = (event: KeyboardEvent) => event.metaKey || event.altKey || event.ctrlKey;

    const handleNewLine = () => {
        insertContent('\n');
        setInputCommitStatus(InputCommitStatus.NORMAL_INPUT_DONE);
    };

    const insertContent = (value: string) => {
        const input = inputRef.current;
        if (input) {
            input.insertText(historyIndexRef.current, value);
            input.setSelection(historyIndexRef.current + value.length, 0);
        }
    };

    const handleFocus = () => {
        props.onEditStateChanged?.(props.commentId, true);
        if (!isFocus.current) {
            isFocus.current = true;
            props.onFocus?.();
        }
    };

    const handleBlur = () => {
        if (InputCommitStatus.INVALID === inputCommitStatus) {
            return;
        }
        if (InputCommitStatus.PENDING === inputCommitStatus) {
            setInputCommitStatus(InputCommitStatus.NORMAL_INPUT_DONE);
        }
        props.onEditStateChanged?.(props.commentId, false);
        if (isFocus.current) {
            isFocus.current = false;
            props.onBlur?.();
        }
    };

    const handleConfirm = async () => {
        if (!inputRef.current) return;
        if (inputCommitStatus === InputCommitStatus.COMMITING) return;
        try {
            const length = inputRef.current.getLength();
            if (length > MAX_CONTENT_LENGTH || length === 0) return;

            const content = getStringifyContent();
            // case：输入框为空，直接回车
            if (content === '[]') return;

            setInputCommitStatus(InputCommitStatus.COMMITING);

            if (props.commentId) {
                await props.updateComment?.(props.commentId, props.groupId, content);
            } else {
                await props.addComment?.(props.groupId, content);
                // case: 当批注侧边栏存在滚动条时，添加的批注会加到批注组末尾，需要滚动到可视区
                setTimeout(() => {
                    if (props.commentGroupItemWrapperRef?.current) {
                        const { scrollHeight } = props.commentGroupItemWrapperRef.current;
                        props.commentGroupItemWrapperRef.current.scrollTo({ top: scrollHeight, behavior: 'smooth' });
                    }
                });
            }
            inputRef.current?.setContents([]);
        } catch (error) {
            const content = formatEditorContent(props.content, commentUserInfo);
            inputRef.current?.setContents(content);
            console.error(error);
        } finally {
            props.onEditStateChanged?.(props.commentId, false);
            setInputCommitStatus(InputCommitStatus.DELEGATE_INPUT_DONE);
        }
    };

    const handleCancel = () => {
        props.onEditStateChanged?.(props.commentId, false);
        inputRef.current?.setContents([]);
    };

    const getStringifyContent = (): string => {
        const originalContent = getOriginalContent();
        if (originalContent.length > 0) {
            return JSON.stringify(originalContent)
                .replace(/(\s|&nbsp;|\n|<br>)+$/g, '')
                .replace(/^(\s|&nbsp;|\n|<br>)+/g, '');
        }
        return '[]';
    };

    /**
     * 获取输入框的文本信息
     */
    const getOriginalContent = (): ICommentEditableOps[] => {
        const ops = inputRef.current?.getContents()?.ops;
        // 文本为空的情况下返回空数组
        if (!ops) return [];
        return ops.map((op) => {
            if (!op.insert) {
                return op;
            }
            let text = '';
            const regx = /(^\s*)|(\s*$)/g;
            const { insert } = op;
            if (typeof insert === 'string') {
                text = (op.insert as string).replace(regx, '').trim();
            } else if (typeof insert === 'object' && insert.mention) {
                text = insert.mention.replace(regx, '').trim();
                return {
                    text,
                };
            }
            return {
                text,
            };
        }).filter(op => op.text);
    };

    const renderCommentInputBtn = () => {
        if (!hasContent) return null;
        return <React.Fragment>
            {isOverLength
                ? <div className={Style['comment-input-error-msg']}>
                    {i18n.t('很抱歉，批注内容不能超过{0}个字符。', { 0: MAX_CONTENT_LENGTH })}
                </div>
                : null}
            <div className={Style['comment-input-control']}>
                <div className={Style['comment-input-seperator']}></div>
                <div className={Style['comment-input-button-group']}>
                    <Button className={Style['comment-input-button-cancel']} onClick={handleCancel}>{i18n.t('取消')}</Button>
                    <Button className={Style['comment-input-button-confirm']} type="primary" onClick={handleConfirm} disabled={!!isOverLength}>{i18n.t('确定')}</Button>
                </div>
            </div>
        </React.Fragment>;
    };

    return <div className={Style['comment-input-wrapper']}>
        <div className={classNames({
            [Style['comment-input']]: true,
            [Style['comment-input-readonly']]: props.readOnly,
            [Style['comment-input-editable']]: !props.readOnly,
        })}>
            {renderCommentEditable()}
        </div>
        { renderCommentInputBtn() }
    </div>;
};

export default CommentInput;
